import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '../AppContext'
import { useAsync } from '../hooks/useAsync'
import { PageHeader } from '../components/primitives/PageHeader'
import { Panel } from '../components/primitives/Panel'
import { DataTable, type DataTableColumn } from '../components/primitives/DataTable'
import { Mono } from '../components/primitives/Fact'
import { ActivityOutcomeChip } from '../components/primitives/StatusChip'
import type { ActivityEvent, ActivityResponse } from '../api/types'
import { Notice } from '../components/primitives/Notice'

/** An `ActivityEvent` plus the ordinal-derived React key (see below). */
type ActivityRow = ActivityEvent & { rowKey: string }

const LOOKBACKS = [
  { label: 'Last 24h', hours: 24 },
  { label: 'Last 7 days', hours: 168 },
  { label: 'Last 30 days', hours: 720 },
]

// Operational activity / audit feed (ATC-057). Read-only: recent
// issuance / checkout / settle / denial events from the Licensing App
// Insights, reverse-chronological, with an environment filter + lookback.
//
// ATC-110: the outcome column was three parallel chip idioms of coloured text
// (`.outcome-success` etc.). It is now `ActivityOutcomeChip`, which routes
// through `theme/statusTone.ts` — and that mapping carries one VISIBLE
// BEHAVIOUR CHANGE: `not_found` moves from grey to AMBER. The evidence is
// inside ATC — `licensing-service/Get-ActivityFeed/run.ps1` deliberately
// surfaces `not_found` alongside `denied` and `error` in its KQL filter, so the
// backend already classes it as noteworthy — combined with §1.3's rule that a
// real enum value never falls through to neutral.
export function Activity() {
  const { api } = useApp()
  const [hours, setHours] = useState(168)
  const [ceiInput, setCeiInput] = useState('')
  const [cei, setCei] = useState('')

  const { data, loading, error, reload } = useAsync<ActivityResponse>(
    () => api.getActivity({ hours, customerEnvironmentId: cei || undefined, limit: 200 }),
    [hours, cei],
  )

  // The feed has no natural unique key — two events can share a timestamp to
  // the second — so the ordinal is folded in, exactly as the pre-conversion
  // `key={`${ev.timestamp}-${i}`}` did. `DataTable.keyForRow` takes only the
  // item, so the key is precomputed here rather than lost.
  const rows: ActivityRow[] = (data?.events ?? []).map((ev, i) => ({
    ...ev,
    rowKey: `${ev.timestamp}-${i}`,
  }))

  const columns: DataTableColumn<ActivityRow>[] = [
    {
      key: 'time',
      header: 'Time (UTC)',
      render: (ev) => (
        <span className="nowrap">
          <Mono>{formatTime(ev.timestamp)}</Mono>
        </span>
      ),
    },
    { key: 'service', header: 'Service', render: (ev) => ev.serviceName },
    {
      key: 'operation',
      header: 'Operation',
      // `.audit` is accent TEXT, so it takes the 5.18:1 `--accent-text` rung.
      render: (ev) => (ev.auditEvent ? <span className="audit">{ev.auditEvent}</span> : ev.operation),
    },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (ev) => <ActivityOutcomeChip outcome={ev.outcome} />,
    },
    {
      key: 'environment',
      header: 'Environment',
      // "NO VALUE" IS NOT "MISSING DATA" (§1.3). This was an em-dash, which
      // reads as a data problem; the real absent state is that the event was
      // not scoped to any one customer environment.
      render: (ev) =>
        ev.customerEnvironmentId ? (
          <Link to={`/environments/${ev.customerEnvironmentId}`}>
            <Mono>{ev.customerEnvironmentId}</Mono>
          </Link>
        ) : (
          <span className="muted">Not scoped</span>
        ),
    },
    {
      key: 'detail',
      header: 'Detail',
      render: (ev) => (
        <span className="detail">{describe(ev.errorCode, ev.accountId, ev.migrationId)}</span>
      ),
    },
  ]

  return (
    <section>
      <PageHeader
        title="Activity"
        actions={
          <button onClick={reload} disabled={loading}>
            Refresh
          </button>
        }
      />

      <form
        className="toolbar"
        onSubmit={(e) => {
          e.preventDefault()
          setCei(ceiInput.trim())
        }}
      >
        <input
          type="search"
          placeholder="Filter by environment id…"
          value={ceiInput}
          onChange={(e) => setCeiInput(e.target.value)}
        />
        <select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
          {LOOKBACKS.map((l) => (
            <option key={l.hours} value={l.hours}>
              {l.label}
            </option>
          ))}
        </select>
        <button type="submit">Apply</button>
        {cei && (
          <button
            type="button"
            onClick={() => {
              setCeiInput('')
              setCei('')
            }}
          >
            Clear filter
          </button>
        )}
      </form>

      {error && <Notice tone="error">{error}</Notice>}

      {/* The feed's chips carry status hexes, which fall under the 3:1
          graphical floor on the warm canvas — so the table must sit on white. */}
      <Panel flush>
        <DataTable
          caption="Recent platform activity"
          columns={columns}
          data={rows}
          keyForRow={(ev) => ev.rowKey}
          isLoading={loading}
          error={error ? 'Could not load activity.' : undefined}
          emptyState="No activity in this window."
        />
      </Panel>
    </section>
  )
}

function formatTime(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().replace('T', ' ').slice(0, 19)
}

function describe(errorCode: string | null, accountId: string | null, migrationId: string | null): string {
  const parts: string[] = []
  if (errorCode) parts.push(errorCode)
  if (accountId) parts.push(`account ${accountId}`)
  if (migrationId) parts.push(`migration ${migrationId}`)
  // `·` is a separator in composed prose, not an icon.
  return parts.join(' · ')
}
